/**
 * Phase 20 / DEC-285 — saying which row was dropped as the template's example.
 *
 * The API drops row 2 only when its content IS the grey example row, and reports
 * it: on the upload response per sheet (`exampleRowSkipped`) and on the batch's
 * `error_summary.exampleRowsSkipped`, so history can answer it later. Dropping a
 * row the hauler can see in their own file has to be visible — the positional
 * skip this replaced deleted their first real row and said nothing — so the
 * sentence carries the row's content, which is what lets them disagree with it.
 */

import type { SkippedExampleRow } from './types';

/** Long enough to recognise the row, short enough to stay one line on a phone. */
const MAX_CONTENT_CHARS = 120;

/** The row's non-empty values in file order, e.g. "ELK CITY YARD · 35.4120000 · …". */
export function exampleRowContent(skipped: SkippedExampleRow): string {
  const text = Object.values(skipped.cells)
    .map((v) => v.trim())
    .filter((v) => v !== '')
    .join(' · ');
  return text.length > MAX_CONTENT_CHARS ? `${text.slice(0, MAX_CONTENT_CHARS - 1)}…` : text;
}

export function exampleRowSentence(skipped: SkippedExampleRow): string {
  return `Row ${skipped.rowNumber.toString()} matched the template example and was not imported: ${exampleRowContent(skipped)}`;
}

export interface RecordedExampleRow extends SkippedExampleRow {
  readonly sheetName: string;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * `error_summary.exampleRowsSkipped`, read defensively: the column is free-form
 * jsonb that other writers add keys to, and a batch from before DEC-285 has no
 * such key at all. Anything malformed is left out rather than thrown on — the
 * rest of the detail screen must still render.
 */
export function exampleRowsFromSummary(summary: unknown): readonly RecordedExampleRow[] {
  let s: unknown = summary;
  if (typeof s === 'string') {
    try {
      s = JSON.parse(s) as unknown;
    } catch {
      return [];
    }
  }
  if (!isRecord(s)) return [];
  const list = s['exampleRowsSkipped'];
  if (!Array.isArray(list)) return [];
  const out: RecordedExampleRow[] = [];
  for (const item of list as unknown[]) {
    if (!isRecord(item)) continue;
    const { sheetName, rowNumber, cells } = item;
    if (typeof rowNumber !== 'number' || !isRecord(cells)) continue;
    const strings: Record<string, string> = {};
    for (const [k, v] of Object.entries(cells)) {
      if (typeof v === 'string') strings[k] = v;
    }
    out.push({
      sheetName: typeof sheetName === 'string' ? sheetName : '',
      rowNumber,
      cells: strings,
    });
  }
  return out;
}
